import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, MapPin, Box, Building, Phone, Loader2, AlertCircle, RefreshCw, Check } from 'lucide-react';
import { KIND, loadPoints, filterPoints, clearPointCache } from '../lib/deliveryPoints';

/**
 * Picks a post office or parcel locker code out of HP's live network.
 *
 * The centre code fields used to be bare text inputs with "find codes under HP
 * Delivery Points" underneath, which meant leaving a half-filled label form,
 * reading a code off a card, and typing it back in from memory. This is the
 * same lookup the Delivery Points page runs, narrowed to the one kind of centre
 * the field needs and made clickable.
 *
 * It opens stacked over the create-label modal, and goes into document.body
 * through a portal rather than rendering where it sits in the tree. Its own
 * place in the tree is inside that modal's `<form>`, nested under a
 * `.modal-container` that is `overflow: hidden` and a `.modal-overlay` whose
 * `backdrop-filter` makes it the containing block for fixed descendants --
 * which between them would decide where this dialog lands and what clips it.
 * Out at the body it is simply fixed to the viewport, and its search input
 * belongs to no form, so Enter cannot submit the label behind it.
 */

// Croatia has on the order of a thousand post offices. Rendering every match
// makes the dialog crawl on the first frame, and nobody scrolls a thousand
// rows: past this the answer is to type more, which the footer says.
const MAX_ROWS = 80;

const COPY = {
  [KIND.LOCKER]: {
    title: 'Choose a parcel locker',
    subtitle: 'Paketomat codes, live from Hrvatska Pošta.',
    Icon: Box,
    accent: '#e20613',
    accentBg: '#ffeeee',
    noun: 'parcel locker',
    plural: 'parcel lockers',
  },
  [KIND.POST_OFFICE]: {
    title: 'Choose a post office',
    subtitle: 'A post office is identified by its five-digit number.',
    Icon: Building,
    accent: '#008060',
    accentBg: '#e6f4ea',
    noun: 'post office',
    plural: 'post offices',
  },
};

export default function DeliveryPointPicker({
  isOpen,
  kind,
  onClose,
  onSelect,
  selectedCode = '',
  initialSearch = '',
}) {
  const [points, setPoints] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Bumped by Refresh to re-run the load effect after the cache is dropped.
  const [reloadKey, setReloadKey] = useState(0);
  const searchRef = useRef(null);

  const copy = COPY[kind] ?? COPY[KIND.POST_OFFICE];

  // Loads on open and whenever the field switches between post offices and
  // lockers: the two lists come from separate HP fields and must not mix.
  useEffect(() => {
    if (!isOpen) return undefined;

    let live = true;
    // Seeded with the town this parcel is going to or coming from, with the
    // caret after it: narrowing "Zagreb" further is the common move, and
    // clearing it to browse the whole country is one keystroke away.
    setQuery(initialSearch);
    setError(null);
    setLoading(true);
    searchRef.current?.focus();

    loadPoints(kind)
      .then((list) => live && setPoints(list))
      .catch((err) => {
        if (!live) return;
        setError(err.message);
        setPoints([]);
      })
      .finally(() => live && setLoading(false));

    return () => {
      live = false;
    };
    // initialSearch is a seed read once when the dialog opens, not a
    // controlled value -- the merchant's own typing must not be overwritten if
    // the parent happens to re-render with a different town.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, kind, reloadKey]);

  // Escape dismisses the picker. The modal underneath does not close on
  // Escape, so one press cannot take a half-filled label form with it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onClose]);

  const matches = useMemo(() => filterPoints(points, query), [points, query]);

  if (!isOpen) return null;

  const shown = matches.slice(0, MAX_ROWS);

  const refresh = () => {
    clearPointCache(kind);
    setReloadKey((key) => key + 1);
  };

  const choose = (point) => {
    onSelect(point);
    onClose();
  };

  const { Icon } = copy;

  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-container"
        style={{ maxWidth: '640px' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-point-picker-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div
              style={{
                background: copy.accentBg,
                color: copy.accent,
                padding: '0.4rem',
                borderRadius: '50%',
                display: 'flex',
              }}
            >
              <Icon size={20} />
            </div>
            <div>
              <h3 id="delivery-point-picker-title" style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                {copy.title}
              </h3>
              <p style={{ fontSize: '0.82rem', color: 'var(--color-secondary)' }}>{copy.subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onClose}
            style={{ padding: '0.4rem' }}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="modal-toolbar">
          <div className="picker-toolbar">
            <div className="picker-search">
              <Search
                size={16}
                style={{
                  position: 'absolute',
                  left: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'var(--color-secondary)',
                }}
              />
              <input
                ref={searchRef}
                type="text"
                className="form-control"
                style={{ paddingLeft: '2.3rem' }}
                placeholder={`Search ${copy.plural} by code, town or street...`}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={`Search ${copy.plural}`}
              />
            </div>
            <button type="button" className="btn btn-secondary" onClick={refresh} disabled={loading}>
              <RefreshCw size={14} className={loading ? 'spin' : undefined} /> Refresh
            </button>
          </div>
        </div>

        <div className="modal-body">
          {error && (
            <div
              style={{
                background: '#fce8e6',
                border: '1px solid #f3b8b4',
                color: '#c5221f',
                padding: '0.75rem 1rem',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.85rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.5rem',
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              <span>Could not reach Hrvatska Pošta: {error}</span>
            </div>
          )}

          {loading && !error && (
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
              <Loader2 size={28} className="spin" style={{ color: 'var(--color-brand-hp)' }} />
              <p style={{ color: 'var(--color-secondary)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                Loading the HP delivery network...
              </p>
            </div>
          )}

          {!loading && !error && matches.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
              <MapPin size={28} style={{ color: 'var(--color-secondary)' }} />
              <p style={{ color: 'var(--color-secondary)', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                {points.length === 0
                  ? `Hrvatska Pošta returned no ${copy.plural}.`
                  : `No ${copy.plural} match “${query}”.`}
              </p>
            </div>
          )}

          {!loading && !error && matches.length > 0 && (
            <div className="point-list">
              {shown.map((point, index) => {
                const isSelected = Boolean(point.code) && point.code === selectedCode;
                return (
                  <button
                    key={`${point.code}-${index}`}
                    type="button"
                    className={`point-row${isSelected ? ' is-selected' : ''}`}
                    onClick={() => choose(point)}
                    aria-current={isSelected ? 'true' : undefined}
                  >
                    <span className="point-row-code">{point.code || '--'}</span>
                    <span className="point-row-body">
                      <span className="point-row-name">{point.name}</span>
                      <span className="point-row-meta">
                        <MapPin size={12} style={{ color: copy.accent, flexShrink: 0 }} />
                        {point.address}, {point.zip} {point.city}
                      </span>
                      {point.tel && (
                        <span className="point-row-meta">
                          <Phone size={11} style={{ flexShrink: 0 }} /> {point.tel}
                        </span>
                      )}
                    </span>
                    {isSelected && (
                      <Check size={16} style={{ color: 'var(--color-shopify-green)', flexShrink: 0 }} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8rem', color: 'var(--color-secondary)' }}>
            {loading || error
              ? ''
              : matches.length > shown.length
                ? `Showing ${shown.length} of ${matches.length} ${copy.plural} — keep typing to narrow it down.`
                : `${matches.length} ${matches.length === 1 ? copy.noun : copy.plural}`}
          </span>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
